import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  InputAdornment,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  MenuItem,
  CircularProgress,
  Tooltip
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Computer as ComputerIcon,
  Business as BusinessIcon,
  PictureAsPdf as PictureAsPdfIcon,
  PlayCircleFilled as PlayCircleFilledIcon,
  AssignmentLate as AssignmentLateIcon,
  LocationOn as LocationOnIcon,
  Refresh as RefreshIcon
} from "@material-ui/icons";

import api from "../../services/api";
import { toast } from "react-toastify";

import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

// Inicialização segura do VFS do pdfMake
if (pdfFonts) {
  if (pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) {
    pdfMake.vfs = pdfFonts.pdfMake.vfs;
  } else if (pdfFonts.vfs) {
    pdfMake.vfs = pdfFonts.vfs;
  }
}

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  filterSection: {
    marginBottom: theme.spacing(3),
  },
  tableHeader: {
    backgroundColor: "#34495e",
  },
  headerCell: {
    color: "#fff",
    fontWeight: "bold",
  },
}));

// Funções utilitárias de leitura segura
const safeString = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val.toLowerCase();
  if (typeof val === "number") return String(val).toLowerCase();
  if (typeof val === "object") {
    if (val.name) return String(val.name).toLowerCase();
    if (val.cidade) return String(val.cidade).toLowerCase();
    if (val.nome) return String(val.nome).toLowerCase();
  }
  return "";
};

const getClienteCnpj = (cliente) => {
  if (!cliente) return "-";
  return (
    cliente.cnpjOrCpf ||
    cliente.cnpjCpf || 
    cliente.cnpjOuCpf || 
    cliente.cnpj || 
    cliente.cpf ||
    "-"
  );
};

const renderCityName = (cityField) => {
  if (!cityField) return "N/A";
  if (typeof cityField === "string") return cityField;
  if (typeof cityField === "object") {
    return cityField.name || cityField.cidade || cityField.nome || "N/A";
  }
  return String(cityField);
};

// COMPONENTE ISOLADO DO CLIENTE
const ClienteItem = memo(({
  cliente,
  classes,
  onEditarCliente,
  onExcluirCliente,
  onAdicionarPendencia,
  onTogglePendencia,
  onExcluirPendencia,
  onNovoAcesso,
  onEditarAcesso,
  onApagarAcesso
}) => {
  const [textoPendencia, setTextoPendencia] = useState("");

  const clienteId = cliente.id || cliente._id;
  const nomeCli = cliente.name || cliente.clienteNome || "Sem nome";
  const cnpj = getClienteCnpj(cliente);
  const acessos = Array.isArray(cliente.accesses) ? cliente.accesses : Array.isArray(cliente.acessos) ? cliente.acessos : [];
  const pendencias = Array.isArray(cliente.pendencies) ? cliente.pendencies : Array.isArray(cliente.pendenciasPosVenda) ? cliente.pendenciasPosVenda : [];

  const handleAddPendencia = () => {
    if (!textoPendencia.trim()) return;
    onAdicionarPendencia(clienteId, textoPendencia);
    setTextoPendencia("");
  };

  return (
    <Accordion style={{ marginBottom: 12 }} TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" justifyContent="space-between" width="100%" pr={2} alignItems="center">
          <Box display="flex" flexDirection="column">
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {nomeCli}
            </Typography>
            <Box display="flex" gap={1} alignItems="center" mt={0.5} flexWrap="wrap">
              <Chip
                label={`Sistema: ${cliente.systemType || cliente.tipoSistema || "N/A"}`}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip
                icon={<LocationOnIcon style={{ fontSize: 14 }} />}
                label={renderCityName(cliente.city || cliente.cidade)}
                size="small"
                style={{ backgroundColor: "#eef2f5" }}
              />
              <Typography variant="caption" color="textSecondary">
                CNPJ/CPF: {cnpj}
              </Typography>
            </Box>
          </Box>
          <Chip label={`${acessos.length} Acesso(s)`} size="small" />
        </Box>
      </AccordionSummary>

      <AccordionDetails style={{ flexDirection: "column" }} onClick={(e) => e.stopPropagation()}>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Box display="flex" gap={1}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => onEditarCliente(cliente)}
            >
              Editar Cliente
            </Button>
            <Button
              size="small"
              color="secondary"
              startIcon={<DeleteIcon />}
              onClick={() => onExcluirCliente(clienteId)}
            >
              Excluir Cliente
            </Button>
          </Box>

          {cliente.playlistUrl && (
            <Button
              size="small"
              variant="contained"
              style={{ backgroundColor: "#FF0000", color: "#FFF" }}
              startIcon={<PlayCircleFilledIcon />}
              onClick={() => window.open(cliente.playlistUrl, "_blank")}
            >
              Abrir Playlist
            </Button>
          )}
        </Box>

        <Grid container spacing={2} style={{ marginBottom: 12 }}>
          {(cliente.notes || cliente.observacoesGerais) && (
            <Grid item xs={12}>
              <Box p={1.5} style={{ backgroundColor: "#f8f9fa", borderRadius: 6, borderLeft: "4px solid #3498db" }}>
                <Typography variant="caption" color="textSecondary"><b>Observações do Cliente:</b></Typography>
                <Typography variant="body2">{cliente.notes || cliente.observacoesGerais}</Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        <Divider style={{ margin: "12px 0" }} />

        {/* PENDÊNCIAS */}
        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <AssignmentLateIcon color="action" fontSize="small" />
            <Typography variant="subtitle2"><b>Pendências de Pós-Venda</b></Typography>
          </Box>

          <Box display="flex" gap={1} mb={1}>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder="Adicionar nova pendência..."
              value={textoPendencia}
              onChange={(e) => setTextoPendencia(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddPendencia()}
            />
            <Button variant="contained" color="primary" onClick={handleAddPendencia}>
              Adicionar
            </Button>
          </Box>

          <List dense>
            {pendencias.length === 0 ? (
              <Typography variant="caption" color="textSecondary">
                Nenhuma pendência cadastrada.
              </Typography>
            ) : (
              pendencias.map((p) => {
                const pId = p.id || p._id;
                const isDone = Boolean(p.isFinished || p.done || p.concluido);

                return (
                  <ListItem 
                    key={pId} 
                    button 
                    onClick={() => onTogglePendencia(p)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={isDone}
                        tabIndex={-1}
                        disableRipple
                        onChange={() => onTogglePendencia(p)}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={p.text || p.texto}
                      style={{ textDecoration: isDone ? "line-through" : "none" }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        size="small" 
                        onClick={() => onExcluirPendencia(pId)}
                      >
                        <DeleteIcon fontSize="small" color="secondary" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })
            )}
          </List>
        </Box>

        <Divider style={{ margin: "12px 0" }} />

        {/* TABELA DE ACESSOS */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2"><b>Conexões de Acesso Remoto</b></Typography>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => onNovoAcesso(clienteId)}
          >
            Adicionar Acesso
          </Button>
        </Box>

        <Table size="small">
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell className={classes.headerCell}>Tipo</TableCell>
              <TableCell className={classes.headerCell}>Nome / Terminal</TableCell>
              <TableCell className={classes.headerCell}>ID / Conexão</TableCell>
              <TableCell className={classes.headerCell}>Senha</TableCell>
              <TableCell className={classes.headerCell}>Observação</TableCell>
              <TableCell align="center" className={classes.headerCell}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {acessos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">Nenhum acesso cadastrado.</TableCell>
              </TableRow>
            ) : (
              acessos.map((acc) => {
                const accId = acc.id || acc._id;
                return (
                  <TableRow key={accId} hover>
                    <TableCell><Chip label={acc.type} size="small" variant="outlined" color="primary" /></TableCell>
                    <TableCell><b>{acc.notes || "-"}</b></TableCell>
                    <TableCell>{acc.accessId}</TableCell>
                    <TableCell>{acc.password || "-"}</TableCell>
                    <TableCell>{acc.observation || "-"}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="primary" onClick={() => onEditarAcesso(clienteId, acc)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="secondary" onClick={() => onApagarAcesso(accId)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
});

export default function AcessosRemotos() {
  const classes = useStyles();
  const [clientesAcessos, setClientesAcessos] = useState([]);
  const [listaCidades, setListaCidades] = useState([]);
  const [busca, setBusca] = useState("");
  const [loading, setLoading] = useState(false);

  // Modais
  const [openClienteModal, setOpenClienteModal] = useState(false);
  const [openAcessoModal, setOpenAcessoModal] = useState(false);
  const [clienteEditandoId, setClienteEditandoId] = useState(null);
  const [clienteSelecionadoAcesso, setClienteSelecionadoAcesso] = useState(null);
  const [acessoEditandoId, setAcessoEditandoId] = useState(null);

  // Formulário Cliente
  const [nomeCliente, setNomeCliente] = useState("");
  const [cnpjCpf, setCnpjCpf] = useState("");
  const [cidadeSelecionada, setCidadeSelecionada] = useState(null);
  const [tipoSistema, setTipoSistema] = useState("Gestor");
  const [playlistUrl, setPlaylistUrl] = useState("");
  const [observacoesGerais, setObservacoesGerais] = useState("");
  const [dataUltimoFeedback, setDataUltimoFeedback] = useState("");
  const [observacaoFeedback, setObservacaoFeedback] = useState("");

  // Formulário Acesso
  const [tipo, setTipo] = useState("AnyDesk");
  const [accessId, setAccessId] = useState("");
  const [password, setPassword] = useState("");
  const [notes, setNotes] = useState("");
  const [observation, setObservation] = useState("");

  const carregarClientes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setClientesAcessos(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Erro ao carregar a lista de clientes e acessos.");
    } finally {
      setLoading(false);
    }
  }, []);

  const carregarCidades = useCallback(async () => {
    try {
      const { data } = await api.get("/cities");
      setListaCidades(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erro ao carregar lista de cidades:", err);
    }
  }, []);

  useEffect(() => {
    carregarClientes();
    carregarCidades();
  }, [carregarClientes, carregarCidades]);

  // AÇÕES DO CLIENTE
  const handleOpenModalNovoCliente = () => {
    setClienteEditandoId(null);
    setNomeCliente("");
    setCnpjCpf("");
    setCidadeSelecionada(null);
    setTipoSistema("Gestor");
    setPlaylistUrl("");
    setObservacoesGerais("");
    setDataUltimoFeedback("");
    setObservacaoFeedback("");
    setOpenClienteModal(true);
  };

  const handleOpenModalEditarCliente = useCallback((cliente) => {
    setClienteEditandoId(cliente.id || cliente._id);
    setNomeCliente(cliente.name || cliente.clienteNome || "");
    
    // Pega o valor existente de cnpjOrCpf (ou variações)
    const valCnpj = getClienteCnpj(cliente);
    setCnpjCpf(valCnpj === "-" ? "" : valCnpj);

    const cityObj = listaCidades.find((c) => {
      const cityIdCliente = cliente.cityId || cliente.city?.id;
      if (cityIdCliente && c.id === cityIdCliente) return true;

      const cityNameCliente = renderCityName(cliente.city || cliente.cidade);
      return (c.name || c.cidade || c.nome || "").toLowerCase() === cityNameCliente.toLowerCase();
    });

    setCidadeSelecionada(cityObj || null);
    setTipoSistema(cliente.systemType || cliente.tipoSistema || "Gestor");
    setPlaylistUrl(cliente.playlistUrl || "");
    setObservacoesGerais(cliente.notes || cliente.observacoesGerais || "");
    setDataUltimoFeedback(cliente.lastFeedbackDate || cliente.dataUltimoFeedback || "");
    setObservacaoFeedback(cliente.feedbackNotes || cliente.observacaoFeedback || "");
    setOpenClienteModal(true);
  }, [listaCidades]);

  const handleSalvarCliente = async () => {
    if (!nomeCliente.trim()) {
      toast.warn("Informe o nome do cliente.");
      return;
    }

    const cityName = cidadeSelecionada
      ? (cidadeSelecionada.name || cidadeSelecionada.cidade || cidadeSelecionada.nome || "-")
      : "-";

    // Enviando explicitamente a propriedade cnpjOrCpf esperada pelo seu backend
    const payload = {
      name: nomeCliente,
      cnpjOrCpf: cnpjCpf || "",
      cityId: cidadeSelecionada ? cidadeSelecionada.id : null,
      city: cityName,
      cidade: cityName,
      systemType: tipoSistema,
      playlistUrl,
      notes: observacoesGerais,
      lastFeedbackDate: dataUltimoFeedback,
      feedbackNotes: observacaoFeedback
    };

    try {
      if (clienteEditandoId) {
        await api.put(`/clients/${clienteEditandoId}`, payload);
        toast.success("Cliente atualizado com sucesso!");
      } else {
        await api.post("/clients", payload);
        toast.success("Cliente cadastrado com sucesso!");
      }
      setOpenClienteModal(false);
      carregarClientes();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao salvar dados do cliente.");
    }
  };

  const handleExcluirCliente = useCallback(async (clienteId) => {
    if (window.confirm("Deseja realmente apagar este cliente e todos os seus dados?")) {
      try {
        await api.delete(`/clients/${clienteId}`);
        toast.success("Cliente excluído!");
        carregarClientes();
      } catch (err) {
        toast.error("Erro ao excluir cliente.");
      }
    }
  }, [carregarClientes]);

  // PENDÊNCIAS
  const handleAdicionarPendencia = useCallback(async (clientId, text) => {
    try {
      const { data } = await api.post("/client-pendencies", { clientId, text });
      toast.success("Pendência adicionada!");
      
      setClientesAcessos((prev) =>
        prev.map((cli) => {
          const cId = cli.id || cli._id;
          if (cId === clientId) {
            const currentPendencies = cli.pendencies || cli.pendenciasPosVenda || [];
            return {
              ...cli,
              pendencies: [...currentPendencies, data],
              pendenciasPosVenda: [...currentPendencies, data]
            };
          }
          return cli;
        })
      );
    } catch (err) {
      toast.error("Erro ao adicionar pendência.");
    }
  }, []);

  const handleTogglePendencia = useCallback(async (pendencia) => {
    const pendenciaId = typeof pendencia === "object" ? (pendencia.id || pendencia._id) : pendencia;
    
    const isDoneAtual = Boolean(pendencia.isFinished || pendencia.done || pendencia.concluido);
    const novoStatus = !isDoneAtual;

    setClientesAcessos((prevClientes) =>
      prevClientes.map((cliente) => ({
        ...cliente,
        pendencies: (cliente.pendencies || cliente.pendenciasPosVenda || []).map((p) => {
          const pId = p.id || p._id;
          if (pId === pendenciaId) {
            return { ...p, isFinished: novoStatus, done: novoStatus, concluido: novoStatus };
          }
          return p;
        }),
        pendenciasPosVenda: (cliente.pendenciasPosVenda || []).map((p) => {
          const pId = p.id || p._id;
          if (pId === pendenciaId) {
            return { ...p, isFinished: novoStatus, done: novoStatus, concluido: novoStatus };
          }
          return p;
        })
      }))
    );

    try {
      await api.patch(`/client-pendencies/${pendenciaId}/toggle`, {
        isFinished: novoStatus,
        done: novoStatus,
        concluido: novoStatus
      });
    } catch (err) {
      try {
        await api.put(`/client-pendencies/${pendenciaId}`, {
          isFinished: novoStatus,
          done: novoStatus,
          concluido: novoStatus
        });
      } catch (error) {
        toast.error("Erro ao atualizar status da pendência.");
        carregarClientes();
      }
    }
  }, [carregarClientes]);

  const handleExcluirPendencia = useCallback(async (pendenciaId) => {
    setClientesAcessos((prev) =>
      prev.map((cli) => ({
        ...cli,
        pendencies: (cli.pendencies || cli.pendenciasPosVenda || []).filter(
          (p) => (p.id || p._id) !== pendenciaId
        ),
        pendenciasPosVenda: (cli.pendenciasPosVenda || cli.pendencies || []).filter(
          (p) => (p.id || p._id) !== pendenciaId
        )
      }))
    );

    try {
      await api.delete(`/client-pendencies/${pendenciaId}`);
      toast.success("Pendência removida!");
    } catch (err) {
      toast.error("Erro ao excluir pendência.");
      carregarClientes();
    }
  }, [carregarClientes]);

  // ACESSOS
  const handleOpenModalNovoAcesso = useCallback((clientId) => {
    setClienteSelecionadoAcesso(clientId);
    setAcessoEditandoId(null);
    setTipo("AnyDesk");
    setAccessId("");
    setPassword("");
    setNotes("");
    setObservation("");
    setOpenAcessoModal(true);
  }, []);

  const handleOpenModalEditarAcesso = useCallback((clientId, acesso) => {
    setClienteSelecionadoAcesso(clientId);
    setAcessoEditandoId(acesso.id || acesso._id);
    setTipo(acesso.type);
    setAccessId(acesso.accessId);
    setPassword(acesso.password || "");
    setNotes(acesso.notes || "");
    setObservation(acesso.observation || "");
    setOpenAcessoModal(true);
  }, []);

  const handleSalvarAcesso = async () => {
    if (!accessId.trim()) {
      toast.warn("Informe o ID de acesso.");
      return;
    }

    const payload = {
      clientId: clienteSelecionadoAcesso,
      type: tipo,
      accessId,
      password,
      notes,
      observation
    };

    try {
      if (acessoEditandoId) {
        await api.put(`/client-accesses/${acessoEditandoId}`, payload);
        toast.success("Acesso remoto atualizado!");
      } else {
        await api.post("/client-accesses", payload);
        toast.success("Acesso remoto cadastrado!");
      }
      setOpenAcessoModal(false);
      carregarClientes();
    } catch (err) {
      toast.error("Erro ao salvar acesso remoto.");
    }
  };

  const handleApagarAcesso = useCallback(async (acessoId) => {
    if (window.confirm("Deseja apagar este acesso remoto?")) {
      try {
        await api.delete(`/client-accesses/${acessoId}`);
        toast.success("Acesso removido!");
        carregarClientes();
      } catch (err) {
        toast.error("Erro ao apagar acesso remoto.");
      }
    }
  }, [carregarClientes]);

  // FILTRO COM MEMORIZAÇÃO
  const clientesFiltrados = useMemo(() => {
    const termo = safeString(busca);
    if (!termo) return clientesAcessos;

    return clientesAcessos.filter((c) => {
      const nome = safeString(c.name || c.clienteNome);
      const cnpj = safeString(getClienteCnpj(c));
      const cidadeStr = safeString(c.city || c.cidade);
      const sis = safeString(c.systemType || c.tipoSistema);

      const bateuCliente =
        nome.includes(termo) ||
        cnpj.includes(termo) ||
        cidadeStr.includes(termo) ||
        sis.includes(termo);

      const acessosList = Array.isArray(c.accesses)
        ? c.accesses
        : Array.isArray(c.acessos)
        ? c.acessos
        : [];

      const bateuAcesso = acessosList.some(
        (a) =>
          safeString(a.accessId).includes(termo) ||
          safeString(a.type).includes(termo) ||
          safeString(a.notes).includes(termo)
      );

      return bateuCliente || bateuAcesso;
    });
  }, [clientesAcessos, busca]);

  // EXPORTAR PDF
  const handleExportarPDF = () => {
    const bodyTable = [
      [
        { text: "Cliente", style: "tableHeader" },
        { text: "Cidade", style: "tableHeader" },
        { text: "Sistema", style: "tableHeader" },
        { text: "Tipo", style: "tableHeader" },
        { text: "Nome / Terminal", style: "tableHeader" },
        { text: "ID / Conexão", style: "tableHeader" },
        { text: "Senha", style: "tableHeader" },
        { text: "Observação", style: "tableHeader" }
      ]
    ];

    clientesFiltrados.forEach((c) => {
      const nome = c.name || c.clienteNome || "-";
      const cid = renderCityName(c.city || c.cidade);
      const sis = c.systemType || c.tipoSistema || "-";
      const acessos = Array.isArray(c.accesses) ? c.accesses : Array.isArray(c.acessos) ? c.acessos : [];

      if (acessos.length === 0) {
        bodyTable.push([nome, cid, sis, "-", "-", "Sem acessos", "-", "-"]);
      } else {
        acessos.forEach((a) => {
          bodyTable.push([
            nome,
            cid,
            sis,
            a.type || "-",
            a.notes || "-",
            a.accessId || "-",
            a.password || "-",
            a.observation || "-"
          ]);
        });
      }
    });

    const docDefinition = {
      pageSize: "A4",
      pageOrientation: "landscape",
      pageMargins: [20, 20, 20, 20],
      content: [
        { text: "Relatório Geral de Clientes e Acessos Remotos", style: "header" },
        {
          text: `Gerado em: ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}`,
          style: "subheader"
        },
        {
          table: {
            headerRows: 1,
            widths: ["18%", "12%", "10%", "10%", "15%", "13%", "10%", "12%"],
            body: bodyTable
          },
          layout: {
            fillColor: (rowIndex) => (rowIndex === 0 ? "#34495e" : rowIndex % 2 === 0 ? "#F8FAFC" : null)
          }
        }
      ],
      styles: {
        header: { fontSize: 16, bold: true, color: "#34495e", margin: [0, 0, 0, 4] },
        subheader: { fontSize: 9, color: "#7f8c8d", margin: [0, 0, 0, 12] },
        tableHeader: { bold: true, fontSize: 9, color: "#FFFFFF", alignment: "left" }
      },
      defaultStyle: { fontSize: 8 }
    };

    pdfMake.createPdf(docDefinition).download("clientes_acessos_remotos.pdf");
  };

  return (
    <Container maxWidth="lg" className={classes.container}>
      {/* CABEÇALHO */}
      <div className={classes.header}>
        <Typography variant="h5" style={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 8 }}>
          <ComputerIcon color="primary" /> Gerenciador de Clientes e Acessos Remotos
        </Typography>

        <div className={classes.headerActions}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<BusinessIcon />}
            onClick={handleOpenModalNovoCliente}
          >
            Novo Cliente
          </Button>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportarPDF}
          >
            Exportar PDF
          </Button>

          <Tooltip title="Atualizar dados">
            <span>
              <IconButton onClick={carregarClientes} color="primary" disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* FILTRO */}
      <div className={classes.filterSection}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Pesquisar por Cliente, CNPJ/CPF, Cidade, Terminal, ID de Acesso..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          style={{ backgroundColor: "#fff" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
        />
      </div>

      {/* LISTAGEM */}
      {loading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      ) : clientesFiltrados.length === 0 ? (
        <Paper style={{ padding: 24, textAlign: "center" }}>
          <Typography color="textSecondary">Nenhum cliente ou acesso encontrado.</Typography>
        </Paper>
      ) : (
        clientesFiltrados.map((cliente) => (
          <ClienteItem
            key={cliente.id || cliente._id}
            cliente={cliente}
            classes={classes}
            onEditarCliente={handleOpenModalEditarCliente}
            onExcluirCliente={handleExcluirCliente}
            onAdicionarPendencia={handleAdicionarPendencia}
            onTogglePendencia={handleTogglePendencia}
            onExcluirPendencia={handleExcluirPendencia}
            onNovoAcesso={handleOpenModalNovoAcesso}
            onEditarAcesso={handleOpenModalEditarAcesso}
            onApagarAcesso={handleApagarAcesso}
          />
        ))
      )}

      {/* MODAL CLIENTE */}
      <Dialog open={openClienteModal} onClose={() => setOpenClienteModal(false)} fullWidth maxWidth="sm">
        <DialogTitle>{clienteEditandoId ? "Editar Cliente" : "Cadastrar Novo Cliente"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth variant="outlined" label="Nome do Cliente" value={nomeCliente} onChange={(e) => setNomeCliente(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth variant="outlined" label="CNPJ ou CPF" value={cnpjCpf} onChange={(e) => setCnpjCpf(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={listaCidades}
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  return option?.name || option?.cidade || option?.nome || "";
                }}
                value={cidadeSelecionada}
                onChange={(event, newValue) => {
                  setCidadeSelecionada(newValue);
                }}
                noOptionsText="Nenhuma cidade encontrada"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cidade / UF"
                    variant="outlined"
                    fullWidth
                    placeholder="Pesquise a cidade..."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth variant="outlined" label="Tipo de Sistema" value={tipoSistema} onChange={(e) => setTipoSistema(e.target.value)}>
                <MenuItem value="Compilart">Compilart</MenuItem>
                <MenuItem value="Gestor">Gestor</MenuItem>
                <MenuItem value="Sia">Sia</MenuItem>
                <MenuItem value="Outros">Outros</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                label="Data do Último Feedback"
                InputLabelProps={{ shrink: true }}
                value={dataUltimoFeedback}
                onChange={(e) => setDataUltimoFeedback(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Obs. do Feedback"
                value={observacaoFeedback}
                onChange={(e) => setObservacaoFeedback(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth variant="outlined" label="Link da Playlist" value={playlistUrl} onChange={(e) => setPlaylistUrl(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} variant="outlined" label="Observações Geral" value={observacoesGerais} onChange={(e) => setObservacoesGerais(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenClienteModal(false)}>Cancelar</Button>
          <Button onClick={handleSalvarCliente} color="primary" variant="contained">
            Salvar Cliente
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL ACESSO REMOTO */}
      <Dialog open={openAcessoModal} onClose={() => setOpenAcessoModal(false)} fullWidth maxWidth="xs">
        <DialogTitle>{acessoEditandoId ? "Editar Acesso Remoto" : "Cadastrar Acesso Remoto"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth variant="outlined" label="Tipo de Conexão" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                <MenuItem value="AnyDesk">AnyDesk</MenuItem>
                <MenuItem value="TeamViewer">TeamViewer</MenuItem>
                <MenuItem value="RustDesk">RustDesk</MenuItem>
                <MenuItem value="RDP (Windows)">RDP (Windows)</MenuItem>
                <MenuItem value="Outro">Outro</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth variant="outlined" label="Nome do Terminal / Acesso" placeholder="Ex: Caixa 01, Servidor" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth variant="outlined" label="ID de Acesso / Conexão" value={accessId} onChange={(e) => setAccessId(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth variant="outlined" label="Senha" value={password} onChange={(e) => setPassword(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} variant="outlined" label="Observação" value={observation} onChange={(e) => setObservation(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAcessoModal(false)}>Cancelar</Button>
          <Button onClick={handleSalvarAcesso} color="primary" variant="contained">
            Salvar Acesso
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}